import axios from 'axios'
import { api } from './api'
import i18n from '@/i18n'

export const CHAT_MESSAGE_LIMIT = 2000
const CHAT_HISTORY_LIMIT = 10
const CHAT_TIMEOUT_MS = 55000

export interface ChatHistoryEntry {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatSource {
  id: string
  kind: 'page' | 'session' | 'video'
  title: string
  url: string
  description?: string
}

export interface SessionContact {
  sessionId: number
  sessionTitle: string
  email: string | null
  name?: string | null
  phone?: string | null
}

export interface ChatResponse {
  answer: string
  sources: ChatSource[]
  warnings: string[]
  sessionContacts?: SessionContact[]
  sessionOptions?: SessionOption[]
  nextSessionPage?: SessionPage
}

export interface SessionPage {
  title: string
  offset: number
}

function isSessionPage(value: unknown): value is SessionPage {
  return isRecord(value) && typeof value.title === 'string' && value.title.trim().length > 0 &&
    value.title.length <= 150 && Number.isSafeInteger(value.offset) && Number(value.offset) >= 0 && Number(value.offset) <= 2147483596
}

export interface SessionOption {
  sessionId: number
  title: string
  startDate: string | null
  endDate: string | null
  siteName: string
  roomName: string
}

function isSessionOption(value: unknown): value is SessionOption {
  return isRecord(value) && Number.isSafeInteger(value.sessionId) && Number(value.sessionId) > 0 &&
    typeof value.title === 'string' && typeof value.siteName === 'string' && typeof value.roomName === 'string' &&
    [value.startDate, value.endDate].every(date => date === null ||
      (typeof date === 'string' && Number.isFinite(Date.parse(date))))
}

export function isValidChatMessage(message: string): boolean {
  return message.trim().length > 0 && message.length <= CHAT_MESSAGE_LIMIT
}

// Copy and bound the wire history without changing the displayed conversation.
export function boundChatHistory(history: ChatHistoryEntry[]): ChatHistoryEntry[] {
  return history
    .filter(entry => (entry.role === 'user' || entry.role === 'assistant') && entry.content.trim())
    .slice(-CHAT_HISTORY_LIMIT)
    .map(entry => ({ role: entry.role, content: entry.content.trim().slice(0, CHAT_MESSAGE_LIMIT) }))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isSource(value: unknown): value is ChatSource {
  return isRecord(value) && typeof value.id === 'string' &&
    typeof value.kind === 'string' && ['page', 'session', 'video'].includes(value.kind) &&
    typeof value.title === 'string' && typeof value.url === 'string' &&
    (value.description === undefined || typeof value.description === 'string')
}

function isSessionContact(value: unknown): value is SessionContact {
  return isRecord(value) && typeof value.sessionId === 'number' &&
    Number.isSafeInteger(value.sessionId) && value.sessionId > 0 &&
    typeof value.sessionTitle === 'string' && (value.email === null || typeof value.email === 'string') &&
    [value.name, value.phone].every(field => field == null || typeof field === 'string')
}

function parseChatResponse(value: unknown): ChatResponse {
  // Reject unusable answers before updating conversation state. Optional metadata
  // can be absent or partially invalid without hiding an otherwise useful answer.
  if (!isRecord(value) || typeof value.answer !== 'string' || !value.answer.trim()) {
    throw new Error('Invalid chatbot response')
  }
  return {
    answer: value.answer,
    sources: Array.isArray(value.sources) ? value.sources.filter(isSource) : [],
    warnings: Array.isArray(value.warnings)
      ? value.warnings.filter((warning): warning is string => typeof warning === 'string' && !!warning.trim())
      : [],
    sessionContacts: Array.isArray(value.sessionContacts) ? value.sessionContacts.filter(isSessionContact) : [],
    sessionOptions: Array.isArray(value.sessionOptions) ? value.sessionOptions.filter(isSessionOption) : [],
    ...(isSessionPage(value.nextSessionPage) ? { nextSessionPage: value.nextSessionPage } : {}),
  }
}

export const chatbotService = {
  ask(message: string, history: ChatHistoryEntry[], signal: AbortSignal, sessionId?: number, sessionPage?: SessionPage): Promise<ChatResponse> {
    if (!isValidChatMessage(message)) {
      return Promise.reject(new Error('Invalid chat message'))
    }
    // api.ts injects the active tenant and optional bearer token. No implicit
    // sessionId unless the user explicitly selects a session.
    return api.post<unknown>('/session/chatbot', {
      message: message.trim(),
      history: boundChatHistory(history),
      language: i18n.resolvedLanguage || i18n.language || 'en',
      ...(sessionId === undefined ? {} : { sessionId }),
      ...(sessionPage === undefined ? {} : { sessionPage }),
    }, { signal, timeout: CHAT_TIMEOUT_MS, skipRetry: true, skipAuthRedirect: true }).then(parseChatResponse)
  },
}

export function chatErrorKey(error: unknown): string {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  switch (status) {
    case 400: return 'chatBotErrorInvalid'
    case 413: return 'chatBotErrorTooLarge'
    case 429: return 'chatBotErrorRateLimit'
    case 502:
    case 503:
    case 504: return 'chatBotErrorTemporary'
    default: return 'chatBotErrorNetwork'
  }
}

export function chatSourceDestination(source: ChatSource): 'internal' | 'external' | null {
  const url = source.url
  if (!url || /[\s\\]/.test(url) || Array.from(url).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null
  if (url.startsWith('/') && !url.startsWith('//')) {
    return source.kind === 'page' || source.kind === 'session' ? 'internal' : 'external'
  }
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? 'external' : null
  } catch {
    return null
  }
}
